import { useCallback, useEffect, useState } from 'react';
import {
  LEGACY_LOGIN_PATH,
  SPA_LOGIN_PATH,
  normaliseSiyaOrigin,
  resolveSignInOriginCandidates,
  resolveSignInUrl,
} from '../utils/siyaDomain';
import { httpFetch } from '@/lib/httpClient';

/**
 * Where to send this visitor to sign in: WHICH main SIYA host, and WHICH of its two login
 * pages. Neither is assumed — both are settled by asking the host itself, via
 * /api/auth/login-path.
 *
 * The path matters because two login pages are live across the fleet (see
 * utils/siyaDomain) and picking the wrong one silently breaks the button: on a migrated
 * host the legacy path hits the SPA router's catch-all, which drops the query string, so
 * returnUrl and logout never reach the login component and the visitor is forwarded
 * straight back to the card they came from.
 *
 * The host matters on the shared `microapps.siya.com`, whose own name identifies no
 * tenant. There the origin is derived from whatever tenant we know, and `<tenant>.siya.com`
 * simply does not exist for every tenant — so the candidates are walked in preference
 * order and the first one the probe can actually reach wins. A host that does not answer
 * is skipped, not treated as the answer.
 *
 * Null until something answers, and null if nothing can — the caller then renders no
 * sign-in button. That is the deliberate choice: a guessed host or path produces a link
 * that navigates, looks like it worked, and returns the user to where they started, which
 * is the failure this whole path exists to remove. A missing button is visible; a lying
 * one is not.
 *
 * But "no button" is a real cost, not a free safe default — it is an Access Denied card
 * with nothing on it. So everything short of guessing is done first:
 *
 *   - a success is remembered in localStorage, so a returning visitor gets the button on
 *     the first paint with no probe in the way at all;
 *   - a request that failed in transit is retried, because one dropped connection is not
 *     an answer — while a host the server reached and found dead is not retried, it is
 *     abandoned for the next candidate;
 *   - a failure never clears a target we already have.
 *
 * A success is also memoised in module scope for the lifetime of the page — several cards
 * may mount over a session and none should re-ask. A failure is NOT memoised: the next
 * mount tries again.
 */

export interface SignInTarget {
  /**
   * The main SIYA origin that actually serves the login page — and the right "back to
   * SIYA" destination. NOT necessarily the candidate that was asked about: a retired host
   * is followed to wherever it says it has moved, so asking about app.siya.com comes back
   * as synergy.siya.com. Always link to what came back.
   */
  origin: string;
  path: string;
}

// Module scope, so a second card does not repeat the request. Promises are cached too,
// not just results — two cards mounting in the same tick must share one request.
const resolved = new Map<string, SignInTarget>();
const inFlight = new Map<string, Promise<SignInTarget | null>>();

/**
 * Candidates the server has told us it cannot reach, for this page's lifetime only.
 *
 * A dead candidate sits in FRONT of the working one — that is the whole reason the list is
 * walked — so without this every card mount pays the server's full probe budget against a
 * host that is not coming back before it reaches the host that works. Deliberately NOT
 * persisted and not shared with the stored paths: a subdomain that gets created should
 * start working on the next page load, not after a cache expires.
 */
const unreachable = new Set<string>();

/** Upper bound on one call to our own API, which itself spends up to ~8s probing. */
const REQUEST_TIMEOUT_MS = 12_000;

/**
 * One retry, and only for a request that failed in transit — a dropped connection, a pod
 * restarting mid-request. A clean 503 means the server reached its verdict (it could not
 * reach that host), and asking again cannot change it; that candidate is abandoned
 * immediately so the next one is not left waiting behind a host that is simply gone.
 */
const TRANSPORT_ATTEMPTS = 2;
const RETRY_DELAY_MS = 800;

/**
 * How long a remembered target is trusted for the first paint. It is revalidated on every
 * mount regardless, so this only bounds how long a tenant that migrated to the SPA could
 * still be handed yesterday's path by someone who clicks before the revalidation lands.
 */
const STORED_TTL_MS = 12 * 60 * 60 * 1000;

const storageKey = (origin: string) => `siya.loginPath.${origin}`;

/**
 * Only the two paths this app knows about are ever used. The stored value comes back from
 * localStorage, which anything running on this origin can write, and it is interpolated
 * straight into the sign-in URL — without this check that is a path injection into the
 * link we are telling the user is safe to click.
 */
const isKnownLoginPath = (value: unknown): value is string =>
  value === SPA_LOGIN_PATH || value === LEGACY_LOGIN_PATH;

/**
 * The remembered answer for a candidate. The stored ORIGIN is re-validated the same way the
 * live one is — it is a host we are about to send the user to, read back out of storage
 * anything on this page can write, so it has to survive the same allow-list.
 */
function readStoredTarget(candidate: string): SignInTarget | null {
  try {
    const raw = window.localStorage.getItem(storageKey(candidate));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { origin?: unknown; path?: unknown; at?: unknown };
    if (!isKnownLoginPath(parsed?.path)) return null;
    if (typeof parsed.origin !== 'string') return null;
    const origin = normaliseSiyaOrigin(parsed.origin);
    if (!origin) return null;
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at > STORED_TTL_MS) return null;
    return { origin, path: parsed.path };
  } catch {
    return null;
  }
}

function storeTarget(candidate: string, target: SignInTarget): void {
  try {
    window.localStorage.setItem(
      storageKey(candidate),
      JSON.stringify({ ...target, at: Date.now() }),
    );
  } catch {
    // Private mode, or the quota is full. The probe still works; only the head start is lost.
  }
}

/** The first candidate we already have an answer for, so the button can paint at once. */
function knownTarget(candidates: string[]): SignInTarget | null {
  for (const candidate of candidates) {
    const target = resolved.get(candidate) ?? readStoredTarget(candidate);
    if (target) return target;
  }
  return null;
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * 'unreachable' is the server's verdict about the HOST — move to the next candidate.
 * 'failed' is our own request not completing — worth asking again.
 */
type Attempt = { target: SignInTarget } | 'unreachable' | 'failed';

async function requestLoginPath(candidate: string): Promise<Attempt> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const res = await httpFetch(`/api/auth/login-path?origin=${encodeURIComponent(candidate)}`, {
      signal: controller.signal,
    });

    // 503: the server probed and could not reach that host. 400: it will not probe that
    // origin at all. Both are settled answers about this candidate, not transport noise.
    if (res.status === 503 || res.status === 400) return 'unreachable';
    if (!res.ok) return 'failed';

    const body = (await res.json()) as { origin?: unknown; path?: unknown };
    // Only an answer the server actually determined counts: a path we recognise, and an
    // origin that passes the same allow-list the request itself had to pass.
    if (!isKnownLoginPath(body?.path)) return 'unreachable';
    const origin = typeof body.origin === 'string' ? normaliseSiyaOrigin(body.origin) : null;
    return origin ? { target: { origin, path: body.path } } : 'unreachable';
  } catch {
    return 'failed';
  } finally {
    clearTimeout(timer);
  }
}

function fetchLoginPath(candidate: string): Promise<SignInTarget | null> {
  const cached = inFlight.get(candidate);
  if (cached) return cached;

  const request = (async () => {
    for (let attempt = 1; attempt <= TRANSPORT_ATTEMPTS; attempt++) {
      const result = await requestLoginPath(candidate);
      if (result === 'unreachable') {
        unreachable.add(candidate);
        return null;
      }
      if (result !== 'failed') {
        resolved.set(candidate, result.target);
        storeTarget(candidate, result.target);
        return result.target;
      }
      if (attempt < TRANSPORT_ATTEMPTS) await delay(RETRY_DELAY_MS);
    }
    return null;
  })().finally(() => inFlight.delete(candidate));

  inFlight.set(candidate, request);
  return request;
}

/** The candidates in preference order, stopping at the first host that answers. */
async function resolveTarget(candidates: string[]): Promise<SignInTarget | null> {
  for (const candidate of candidates) {
    if (unreachable.has(candidate)) continue;
    const target = await fetchLoginPath(candidate);
    if (target) return target;
  }
  return null;
}

/**
 * The resolved sign-in target: the URL to link to, and the origin it belongs to. The
 * origin is exposed because it is also the only main SIYA host we know is real, which is
 * what a "Go Back to SIYA" action needs on the shared micro-apps host.
 */
/** Drop per-candidate "unreachable" marks so a card's Try Again can re-walk the list. */
export function invalidateSignInProbe(): void {
  unreachable.clear();
}

export function useSignInTarget(
  tenant: string | null | undefined,
  forceReauth = false,
): {
  url: string | null;
  origin: string | null;
  probeSettled: boolean;
  retryProbe: () => void;
} {
  // Recomputed every render rather than memoised: these read localStorage, cookies and the
  // session tenant, any of which can be filled in after the first paint.
  const candidates = resolveSignInOriginCandidates(tenant);
  // Effects cannot depend on a fresh array identity. The list itself is the dependency.
  const candidateKey = candidates.join('|');

  // Server render produces no candidates, so this is null there and the seed cannot cause
  // a hydration mismatch.
  const [target, setTarget] = useState<SignInTarget | null>(() => knownTarget(candidates));
  const [probeSettled, setProbeSettled] = useState(() => {
    if (candidates.length === 0) return true;
    return (
      knownTarget(candidates) !== null ||
      candidates.some((candidate) => resolved.has(candidate))
    );
  });
  const [probeGeneration, setProbeGeneration] = useState(0);

  const retryProbe = useCallback(() => {
    invalidateSignInProbe();
    setProbeGeneration((n) => n + 1);
  }, []);

  useEffect(() => {
    const list = candidateKey ? candidateKey.split('|') : [];
    if (list.length === 0) {
      setProbeSettled(true);
      return;
    }

    const known = knownTarget(list);
    if (known) setTarget(known);

    const cached = list.some((candidate) => resolved.has(candidate));
    if (cached) {
      setProbeSettled(true);
      // Still revalidate in the background — a migration or outage may have changed the answer.
      let live = true;
      void resolveTarget(list).then((next) => {
        if (live && next) setTarget(next);
      });
      return () => {
        live = false;
      };
    }

    setProbeSettled(false);
    let live = true;
    void resolveTarget(list).then((next) => {
      // A failed probe must not take away a target we already had — that would put the
      // button on screen and then remove it under the user.
      if (!live) return;
      if (next) setTarget(next);
      setProbeSettled(true);
    });
    return () => {
      live = false;
    };
  }, [candidateKey, probeGeneration]);

  return {
    url: resolveSignInUrl(target?.origin ?? null, forceReauth, target?.path ?? null),
    origin: target?.origin ?? null,
    probeSettled,
    retryProbe,
  };
}

/** The sign-in URL alone, for callers that do not need the origin. */
export function useSignInUrl(tenant: string | null | undefined, forceReauth = false): string | null {
  return useSignInTarget(tenant, forceReauth).url;
}
